import type { Workbook, Worksheet } from "exceljs";

export interface ReportTransaction {
  owner?: { name?: string };
  id: number;
  type: "income" | "expense";
  amount: number | string;
  description?: string | null;
  occurred_at?: string | null;
  source?: string | null;
  category?: { name?: string | null } | null;
}

export interface ReportRecurring {
  owner?: { name?: string };
  id: number;
  name: string;
  amount: number | string;
  frequency?: string;
  next_due_date?: string | null;
  category?: { name?: string | null } | null;
}

export interface MonthlyReportData {
  viewLabel?: string;
  month: number;
  year: number;
  transactions: ReportTransaction[];
  recurring: ReportRecurring[];
}

interface ReportRow {
  owner?: string;
  date: Date | null;
  description: string;
  category: string;
  source: string;
  status: string;
  cents: number;
}

export function validateReportPeriod(month: number, year: number) {
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(year) || year < 2000 || year > 2100) {
    throw new Error("Selecione um mês e um ano entre 2000 e 2100.");
  }
}

// Read every page without changing the lists or filters displayed in the app.
export async function loadMonthlyReport(
  month: number,
  year: number,
  fetchTransactions: (params: { month: number; year: number; page: number; per_page: number }) => Promise<{ data: ReportTransaction[]; last_page: number }>,
  fetchRecurring: () => Promise<ReportRecurring[]>
): Promise<MonthlyReportData> {
  validateReportPeriod(month, year);
  const transactions: ReportTransaction[] = [];
  const recurring = await fetchRecurring();
  let page = 1;
  let lastPage = 1;
  do {
    const response = await fetchTransactions({ month, year, page, per_page: 100 });
    if (!Array.isArray(response.data) || !Number.isInteger(response.last_page) || response.last_page < page) {
      throw new Error("Não foi possível obter todos os lançamentos. Tente novamente.");
    }
    transactions.push(...response.data);
    lastPage = response.last_page;
    page += 1;
  } while (page <= lastPage);
  return { month, year, transactions, recurring };
}

function amountInCents(amount: number | string): number {
  const value = Number(amount);
  if (!Number.isFinite(value) || value < 0) throw new Error("Há um valor inválido nos dados do relatório.");
  return Math.round(value * 100);
}

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function recurringRows(items: ReportRecurring[], month: number, year: number): ReportRow[] {
  const rows: ReportRow[] = [];
  const lastDay = new Date(year, month, 0).getDate();
  for (const item of items) {
    const frequency = item.frequency || "monthly";
    const source = ({ monthly: "Mensal", weekly: "Semanal", yearly: "Anual" } as Record<string, string>)[frequency] || "Uma vez";
    const base = { owner: item.owner?.name, description: item.name, category: item.category?.name || "Sem categoria", source, status: "Previsto", cents: amountInCents(item.amount) };
    const anchor = parseDate(item.next_due_date);
    if (!anchor) {
      if (!["monthly", "weekly", "yearly"].includes(frequency)) continue;
      const factor = frequency === "weekly" ? 52 / 12 : frequency === "yearly" ? 1 / 12 : 1;
      rows.push({ ...base, date: null, status: "Estimativa mensal", cents: Math.round(base.cents * factor) });
      continue;
    }
    if (frequency === "weekly") {
      const anchorDay = Math.floor(Date.UTC(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()) / 86400000);
      const startDay = Math.floor(Date.UTC(year, month - 1, 1) / 86400000);
      const first = 1 + ((anchorDay - startDay) % 7 + 7) % 7;
      for (let day = first; day <= lastDay; day += 7) rows.push({ ...base, date: new Date(year, month - 1, day, 12) });
    } else if (frequency === "monthly" || (frequency === "yearly" && anchor.getMonth() === month - 1)) {
      rows.push({ ...base, date: new Date(year, month - 1, Math.min(anchor.getDate(), lastDay), 12) });
    } else if (frequency !== "yearly" && anchor.getFullYear() === year && anchor.getMonth() === month - 1) {
      rows.push({ ...base, date: anchor });
    }
  }
  return rows.sort((a, b) => (a.date?.getTime() || Infinity) - (b.date?.getTime() || Infinity) || a.description.localeCompare(b.description, "pt-BR"));
}

const moneyFormat = '"R$" #,##0.00;[Red]-"R$" #,##0.00;"R$" 0.00';
const ink = "17324D";
const green = "05785D";

function styleHeader(sheet: Worksheet, row: number, columns: number) {
  sheet.getRow(row).height = 28;
  for (let col = 1; col <= columns; col += 1) {
    const cell = sheet.getCell(row, col);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0B355F" } };
    cell.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  }
}

function createDetailSheet(workbook: Workbook, name: string, period: string, rows: ReportRow[]) {
  const sheet = workbook.addWorksheet(name, { views: [{ state: "frozen", ySplit: 5, showGridLines: false }] });
  sheet.columns = [{ width: 16 }, { width: 48 }, { width: 28 }, { width: 19 }, { width: 21 }, { width: 23 }];
  sheet.getCell("A1").value = `Fin • ${name}`;
  sheet.getCell("A1").font = { name: "Arial", size: 16, bold: true, color: { argb: `FF${ink}` } };
  sheet.getRow(1).height = 30;
  sheet.getCell("A2").value = period;
  sheet.getCell("B2").value = name === "Recorrentes" ? "Previsões dos cadastros ativos atuais" : "Lançamentos do mês selecionado";
  sheet.getCell("B3").value = "Fonte: dados da sua conta no Fin";
  sheet.getCell("B3").font = { name: "Arial", size: 10, italic: true, color: { argb: "FF747487" } };
  sheet.getRow(5).values = [name === "Recorrentes" ? "Vencimento" : "Data", "Descrição", "Categoria", name === "Recorrentes" ? "Frequência" : "Origem", "Situação", "Valor (R$)", "Conta de origem"];
  sheet.getColumn(7).width = 24;
  styleHeader(sheet, 5, 7);
  rows.forEach((item, index) => {
    // Excel dates represent the displayed calendar day, without a timezone shift.
    const date = item.date ? new Date(Date.UTC(item.date.getFullYear(), item.date.getMonth(), item.date.getDate())) : null;
    const row = sheet.getRow(index + 6);
    row.values = [date, item.description, item.category, item.source, item.status, item.cents / 100, item.owner || ""];
    row.height = Math.max(26, Math.ceil(item.description.length / 44) * 15 + 8);
    row.eachCell(cell => {
      cell.font = { name: "Arial", size: 11, color: { argb: `FF${ink}` } };
      cell.alignment = { vertical: "middle", wrapText: true };
      if (index % 2 === 0) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F8F6" } };
    });
    row.getCell(1).numFmt = "dd/mm/yyyy";
    row.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
    row.getCell(6).numFmt = moneyFormat;
    row.getCell(6).alignment = { horizontal: "right", vertical: "middle" };
  });
  if (!rows.length) sheet.getCell("B6").value = "Nenhum lançamento neste mês.";
  const lastDataRow = Math.max(6, rows.length + 5);
  const totalRow = lastDataRow + 2;
  const total = rows.reduce((sum, row) => sum + row.cents, 0) / 100;
  sheet.getCell(`E${totalRow}`).value = name === "Recorrentes" ? "TOTAL PREVISTO" : "TOTAL";
  sheet.getCell(`F${totalRow}`).value = { formula: `ROUND(SUM(F6:F${lastDataRow}),2)`, result: total };
  for (let col = 5; col <= 6; col += 1) {
    const cell = sheet.getCell(totalRow, col);
    cell.font = { name: "Arial", size: 11, bold: true, color: { argb: `FF${green}` } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F5ED" } };
  }
  sheet.getCell(`F${totalRow}`).numFmt = moneyFormat;
  sheet.getRow(totalRow).height = 30;
  sheet.autoFilter = `A5:G${lastDataRow}`;
  sheet.pageSetup = { orientation: "landscape", paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0, printTitlesRow: "1:5" };
  return { total, totalRow };
}

export async function createMonthlyReportWorkbook(data: MonthlyReportData): Promise<Workbook> {
  validateReportPeriod(data.month, data.year);
  const { Workbook } = await import(/* webpackChunkName: "excel-export" */ "exceljs");
  const workbook = new Workbook();
  workbook.creator = "Fin";
  workbook.calcProperties.fullCalcOnLoad = true;
  const period = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(new Date(data.year, data.month - 1, 1));
  const summary = workbook.addWorksheet("Resumo", { views: [{ showGridLines: false }] });
  const rows = (type: "income" | "expense") => data.transactions.filter(item => item.type === type).map(item => ({
    owner: item.owner?.name, date: parseDate(item.occurred_at), description: item.description || (type === "income" ? "Entrada" : "Gasto"),
    category: item.category?.name || (type === "income" ? "Entrada" : "Sem categoria"),
    source: ({ api: "Manual", whatsapp: "WhatsApp", voice: "Voz" } as Record<string, string>)[item.source || ""] || "Não informada",
    status: "Lançado", cents: amountInCents(item.amount),
  })).sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
  const income = createDetailSheet(workbook, "Entradas", period, rows("income"));
  const expenses = createDetailSheet(workbook, "Gastos", period, rows("expense"));
  const recurring = createDetailSheet(workbook, "Recorrentes", period, recurringRows(data.recurring, data.month, data.year));
  summary.columns = [{ width: 65 }, { width: 26 }];
  summary.getCell("A1").value = "Fin • Relatório mensal";
  summary.getCell("A1").font = { name: "Arial", size: 16, bold: true, color: { argb: `FF${ink}` } };
  summary.getRow(1).height = 32;
  summary.getCell("A2").value = data.viewLabel ? `${period} · ${data.viewLabel}` : period;
  summary.getRow(4).values = ["Resumo do mês", "Valor (R$)"];
  styleHeader(summary, 4, 2);
  const values = [
    { row: 5, label: "Total de entradas", formula: `'Entradas'!F${income.totalRow}`, result: income.total },
    { row: 6, label: "Saídas • gastos lançados", formula: `'Gastos'!F${expenses.totalRow}`, result: expenses.total },
    { row: 7, label: "Saídas • recorrentes previstos", formula: `'Recorrentes'!F${recurring.totalRow}`, result: recurring.total },
    { row: 8, label: "Total de saídas realizadas", formula: "ROUND(B6,2)", result: expenses.total },
    { row: 10, label: "Saldo dos lançamentos (entradas − gastos)", formula: "ROUND(B5-B6,2)", result: Math.round((income.total - expenses.total) * 100) / 100 },
    { row: 11, label: "Cenário: todas as previsões ainda não pagas", formula: "ROUND(B5-B6-B7,2)", result: Math.round((income.total - expenses.total - recurring.total) * 100) / 100 },
  ];
  values.forEach(item => {
    const row = summary.getRow(item.row);
    row.values = [item.label, { formula: item.formula, result: item.result }];
    row.height = 30;
    row.eachCell(cell => {
      cell.font = { name: "Arial", size: 11, color: { argb: `FF${ink}` }, bold: [5, 8, 11].includes(item.row) };
      cell.alignment = { vertical: "middle" };
    });
    row.getCell(2).numFmt = moneyFormat;
    if ([5, 8, 11].includes(item.row)) row.eachCell(cell => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE3F5ED" } }; });
  });
  [
    "Como ler este relatório",
    "Entradas e gastos incluem todos os lançamentos ativos do mês e ano selecionados.",
    "Recorrentes são previsões dos cadastros ativos atuais; não confirmam pagamentos e não reconstituem cadastros alterados ou excluídos.",
    "Sem vencimento definido, o recorrente usa a estimativa mensal, indicada na coluna Situação.",
    "As saídas realizadas não incluem previsões. O cenário adicional só se aplica quando todas as previsões ainda estão por pagar; não é um saldo confirmado.",
    "Os totais têm fórmulas: ao editar os valores existentes nas abas, o Excel atualiza o resumo.",
  ].forEach((note, index) => {
    const cell = summary.getCell(index + 14, 1);
    cell.value = note;
    cell.font = { name: "Arial", size: 11, bold: index === 0, color: { argb: `FF${ink}` } };
    cell.alignment = { wrapText: true, vertical: "middle" };
    summary.getRow(index + 14).height = index === 0 ? 26 : Math.ceil(note.length / 70) * 16 + 8;
  });
  summary.pageSetup = { paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 1 };
  return workbook;
}

export async function downloadMonthlyReport(data: MonthlyReportData, validView: () => boolean = () => true): Promise<void> {
  const workbook = await createMonthlyReportWorkbook(data);
  const buffer = await workbook.xlsx.writeBuffer();
  if (!validView()) throw new Error("A visão financeira mudou. Gere o relatório novamente.");
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `fin-relatorio-${data.year}-${String(data.month).padStart(2, "0")}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}
